import { EventEmitter, Injectable } from '@angular/core';
import { throwError, Subscription, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { LoaderComponent } from 'src/app/shared/loader/loader.component';
import { MatDialog } from '@angular/material/dialog';
import { Config, LANDING_CONFIGURATION, SLUG_CONVERTER } from '../config/landing-config';

declare var $: any;

@Injectable()
export class HelpersService {
  public timeoutRefresh: any;
  private _refreshToken$: Subscription;
  private env = environment;

  private storageAccessTokenKey = 'of-landing-storage-access-token';
  private storageRefreshTokenKey = 'of-landing-storage-refresh-token';
  private storageExpiresIn = 'of-landing-storage-expires-in';
  config: Config = this.env.controls.unique_environment ? LANDING_CONFIGURATION[SLUG_CONVERTER[window.location.pathname.split('/')[1]]] : LANDING_CONFIGURATION[this.env.serviceSlug];

  constructor(
    protected http: HttpClient,
    protected router: Router,
    private toastrService: ToastrService,
    private dialog: MatDialog
  ) {
    this.setOptionsToastr();
  }

  private setOptionsToastr() {
    this.toastrService.toastrConfig.closeButton = false;
    this.toastrService.toastrConfig.newestOnTop = true;
    this.toastrService.toastrConfig.progressBar = false;
    this.toastrService.toastrConfig.positionClass = 'toast-bottom-center';
    this.toastrService.toastrConfig.preventDuplicates = false;
    this.toastrService.toastrConfig.timeOut = 5000;
    this.toastrService.toastrConfig.extendedTimeOut = 5000;
    this.toastrService.toastrConfig.enableHtml = true;
  }

  get expiresIn(): number {
    return Number(localStorage.getItem(this.storageExpiresIn));
  }

  get accessToken(): string {
    return localStorage.getItem(this.storageAccessTokenKey);
  }

  get refreshToken(): string {
    return localStorage.getItem(this.storageRefreshTokenKey);
  }

  public saveToLocalStorage(res: any) {
    localStorage.setItem(this.storageAccessTokenKey, res.access_token);
    localStorage.setItem(this.storageRefreshTokenKey, res.refresh_token);
    const expiresInValue = String(
      new Date().valueOf() + res.expires_in * 0.95 * 1000,
    );
    localStorage.setItem(this.storageExpiresIn, expiresInValue);
  }

  public startRefreshInterval() {
    if (this.expiresIn) {
      let interval = this.expiresIn - new Date().valueOf();
      interval = interval < 0 ? 0 : interval;
      clearTimeout(this.timeoutRefresh);
      this.timeoutRefresh = setTimeout(() => {
        this.callRefreshTokenOauth();
      }, interval);
    } else {
      this.clear();
    }
  }

  private callRefreshTokenOauth() {
    const urlParams = new URLSearchParams(window.location.search);
    const fromDashboard = urlParams.get('dashboard');
    const id_risorsa = urlParams.get('id_risorsa');
    const mode = urlParams.get('mode');
    const index = urlParams.get('index');

    if (this._refreshToken$ instanceof Subscription) {
      this._refreshToken$.unsubscribe();
    }
    this.clear();

    if (urlParams.has('auth_token')) {
      urlParams.delete('auth_token');
    }

    let updatedUrl = `${window.location.origin}${window.location.pathname}`;
    const paramsArray = [];
    //gestione trasloco
    if (id_risorsa) {
      paramsArray.push(`id_risorsa=${encodeURIComponent(id_risorsa)}`);
      if (mode) {
        paramsArray.push(`mode=${encodeURIComponent(mode)}`);
      }
      if (index) {
        paramsArray.push(`index=${encodeURIComponent(index)}`);
      }
    }
    
    if (paramsArray.length > 0) {
      updatedUrl += '?' + paramsArray.join('&');
    }

    window.history.replaceState({}, document.title, updatedUrl);

    if (fromDashboard) {
      // Costruiamo i parametri di redirect in modo simile
      const redirectParams: Record<string, string | undefined> = {};
      if (id_risorsa) {
        redirectParams['id_risorsa'] = id_risorsa;
        if (mode) {
          redirectParams['mode'] = mode;
        }
        if (index) {
          redirectParams['index'] = index;
        }
      }

           const redirectUri = this.buildDecodedRedirectUri(
          environment.redirectSSOUrl,
          environment.controls.unique_environment ? this.config.data.route : undefined,
          redirectParams
      );

      const finalUrl = this.formatUrlWithParams(
          `${environment.apiUrl}/api2/saml/login`,
          { redirect_uri: decodeURIComponent(redirectUri) }
      );

      window.location.replace(finalUrl);
    } else {
      const fallbackUrl = `${environment.requestsLinkUrl}${environment.funnelHome}`;
      window.location.replace(fallbackUrl);
    }
  }



private buildDecodedRedirectUri(baseUrl: string, path: string | undefined, additionalParams: Record<string, string | undefined>): string {
  const subUrlParams = Object.entries(additionalParams)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join('&');

  const subUrl = path ? `${baseUrl}${path}` : baseUrl;
  return subUrlParams ? `${subUrl}?${subUrlParams}` : subUrl;
}

private formatUrlWithParams(baseUrl: string, params: Record<string, string | undefined>): string {
  const paramString = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
    .join('&');

  return paramString ? `${baseUrl}?${paramString}` : baseUrl;
}


  private clear() {
    localStorage.removeItem(this.storageAccessTokenKey);
    localStorage.removeItem(this.storageRefreshTokenKey);
    localStorage.removeItem(this.storageExpiresIn);
  }

  public handleError(error, customMessage?) {
    let message = '';
    if (customMessage) {
      message = customMessage;
    } else {
      message = error.error.error['message'];
    }
    return this.toastrService.error(message, '', {timeOut : this.config.data.errorTimeout});
  }

  public showSuccess(message, title = null) {
    this.toastrService.toastrConfig.positionClass = 'toast-center-center';
    return this.toastrService.success(message, title)
  }

  public showLoader() {
    this.dialog.open(LoaderComponent, {
      width: '100%',
      height: '100%',
      disableClose: true,
      position: { left: '0', top: '0' },
      panelClass: 'spinnerModal',
    });
  }

  public hideLoader() {
    this.dialog.closeAll();
  }

  public logout() {
    this.clear();
    window.location.replace(
      `${environment.apiUrl}/api2/saml/logout?redirect_uri=${environment.redirectSSOUrl}${environment.controls.unique_environment ? this.config.data.route : ''}`,
    );
  }

  public downloadFile(fileRes, fileName) {
    const blob = new Blob([fileRes], { type: 'application/pdf' });
    if (blob.size !== 0) {
      const file: any = {};
      file.nomeFile = fileName;
      if (navigator.msSaveOrOpenBlob) {
        file.blob = blob;
      } else {
        file.urlFile = window.URL.createObjectURL(blob);
      }
      if (navigator.msSaveOrOpenBlob && file.blob) {
        // ie
        navigator.msSaveOrOpenBlob(file.blob, file.nomeFile);
      } else {
        // altri browser
        const link = document.createElement('a');
        link.href = file.urlFile;
        link.download = file.nomeFile;
        link.click();
        window.open(link.href);
      }
    }
  }

  public open(content) {
    $('#' + content).modal('show');
  }

  public close(content) {
    $('#' + content).modal('hide');
  }

  public pointInfo(results: google.maps.GeocoderResult[]) {
    const ret: any = {};
    const foundAdministrative = this.getInfo(results, ['administrative_area_level_3']);
    const foundLocality = this.getInfo(results, ['locality'])
    ret.comune = foundAdministrative ? foundAdministrative : foundLocality;
    ret.cap = this.getInfo(results, ['postal_code']);
    ret.provincia = this.getInfo(results, ['administrative_area_level_2'], 'short_name');
    ret.country = this.getInfo(results, ['country'], 'short_name');
    ret.street_number = this.getInfo(results, ['street_number']);
    ret.street = this.getInfo(results, ['route', 'town_square']);
    ret.label = results && results[0] ? results[0].formatted_address : '';
    return ret;
  }

  public pointInfoToDynamicAend(results: google.maps.GeocoderResult[], end): any {
    let ret: any = {};
    const foundAdministrative = this.getInfo(results, ['administrative_area_level_3'], 'short_name');
    const foundLocality = this.getInfo(results, ['locality'], 'short_name')
    ret.long_provincia = this.getInfo(results, ['administrative_area_level_2'], 'short_name');
    // ret.comune = this.getInfo(results, ['administrative_area_level_3'], 'short_name');
    ret.comune = foundAdministrative ? foundAdministrative : foundLocality;
    ret.end = end;

    return ret;
  }

  public retComune(address_components) {
    const foundAdministrative = this.getInfoSmall(address_components, ['retComune']);
    const foundLocality = this.getInfoSmall(address_components, ['locality']);
    return foundAdministrative ? foundAdministrative : foundLocality;
  }



  public getInfo(results, type, attr = 'long_name') {
    let ret = '';
    if (results && results.length) {
      for (const result of results) {
        let found = null;
        for (const t of type) {
          found = result.address_components.filter(a => a.types.includes(t))[0];
          if (found) {
            ret = found[attr];
            return ret;
          } else {
            ret = '';
          }
        }
      }
    }
    return ret;
  }

  public getInfoSmall(results, type, attr = "long_name") {
	// fixed: preselect address on click
	if (!results) return ""
	  
    let ret = '';
    let found = null;
    for (const t of type) {
      found = results.filter(a => a.types.includes(t))[0];
      if (found) {
        ret = found[attr];
        return ret;
      } else {
        ret = '';
      }
    }
    return ret;
  }

}
